/**
 * Build Usage Limits — Shared UI builder
 *
 * Extracts configured (non-null) limits from usage data and returns a
 * render-ready array of `UsageLimitRow` objects. Consumers map rows to
 * their own labels and styled components.
 *
 * Accepts any object satisfying `UsageLimitsInput` — both
 * `SubscriptionUsageStats` (subscription domain) and `UsageInfo`
 * (booking domain) satisfy it. `usedMinutes` is computed internally
 * from `max − remaining` so callers don't need to normalise field names.
 *
 * Unconfigured limits (null max = unlimited) are filtered out — the
 * UI should only show rows for constraints the club has enabled.
 *
 * @module @butter/ui/domains/subscriptions/buildUsageLimits
 */

import { roundHours } from '@butter/library/shared'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Which constraint a usage row represents.
 *
 * `activeSessions` is the odd one out: a COUNT of concurrent bookings, not a
 * time period. It is in this union so the exhaustive `Record<LimitPeriod, …>`
 * label maps consumers build fail to compile until they handle it.
 */
export type LimitPeriod = 'active' | 'day' | 'week' | 'month' | 'activeSessions'

/**
 * Minimal input shape for building usage-limit rows.
 *
 * Only requires the `max` and `remaining` fields — `used` is derived
 * internally as `max − remaining`.
 *
 * Both `SubscriptionUsageStats` and `UsageInfo` satisfy this interface
 * without adapters. Note: the active-remaining field name differs between
 * the two types — `SubscriptionUsageStats` uses the standard
 * `remainingMinutesActive` while `UsageInfo` uses the legacy alias
 * `remainingActiveMinutes`. Both are accepted here; the builder prefers
 * `remainingMinutesActive` and falls back to `remainingActiveMinutes`.
 */
export interface UsageLimitsInput {
  maxActiveMinutes: number | null
  maxMinutesPerDay: number | null
  maxMinutesPerWeek: number | null
  maxMinutesPerMonth: number | null
  /** Standard field name (from MinutesRemaining composable on SubscriptionUsageStats) */
  remainingMinutesActive?: number | null
  /** @deprecated Use remainingMinutesActive — retained for UsageInfo compatibility */
  remainingActiveMinutes?: number | null
  remainingMinutesToday: number | null
  remainingMinutesThisWeek: number | null
  remainingMinutesThisMonth: number | null
  /** Session cap — a COUNT (null/absent = unlimited). Not minutes. */
  maxActiveSessions?: number | null
  /** Slots still available (null = unlimited) */
  remainingActiveSessions?: number | null
}

/**
 * A single usage-limit row, ready to render.
 *
 * `maxMinutes` is guaranteed non-null (we only include configured limits).
 * Both minute and hour values are provided so consumers don't need their
 * own conversion helpers.
 */
export interface HoursLimitRow {
  /** Discriminator — narrow on this before reading any value field. */
  unit: 'hours'
  /** Which constraint period this row represents */
  period: 'active' | 'day' | 'week' | 'month'
  /**
   * Effective limit in minutes. Never null — an unconfigured pool produces no
   * row at all — but it MAY be 0: a membership including no time, where every
   * booking bills as overage. Anything dividing by this needs a clamped
   * denominator.
   */
  maxMinutes: number
  /** Minutes consumed in this period (derived from max − remaining) */
  usedMinutes: number
  /** Remaining minutes (null should not occur since max is non-null, but kept for safety) */
  remainingMinutes: number | null
  /** Effective limit converted to hours (2 decimals) */
  maxHours: number
  /** Hours consumed (2 decimals) */
  usedHours: number
  /** Remaining hours (2 decimals, null for safety) */
  remainingHours: number | null
}

/**
 * The session-cap row. Counts, never minutes — there is deliberately no
 * `maxMinutes`/`maxHours` here, so a consumer that forgets to narrow gets a
 * compile error rather than a plausible-looking wrong number.
 */
export interface SessionsLimitRow {
  /** Discriminator — narrow on this before reading any value field. */
  unit: 'sessions'
  period: 'activeSessions'
  /**
   * Effective limit as a count. Never null, and always > 0 — unlike the minute
   * pools, a concurrency ceiling of 0 is rejected at every write boundary,
   * because it would be a block rather than a price.
   */
  maxSessions: number
  /** Slots currently held (derived from max − remaining) */
  usedSessions: number
  /** Slots still available (null for safety; max is non-null) */
  remainingSessions: number | null
}

/**
 * A single usage-limit row, ready to render.
 *
 * A discriminated union on `unit`: hour-based pools and the count-based
 * session pool cannot be read interchangeably.
 */
export type UsageLimitRow = HoursLimitRow | SessionsLimitRow

/**
 * A usage-limit row augmented with booking-preview fields.
 *
 * Extends `UsageLimitRow` with per-period `thisBooking` (covered portion
 * of the booking) and `overage` (amount exceeding the limit).
 */
export interface BookingHoursLimitRow extends HoursLimitRow {
  /** Minutes from this booking that fit within the remaining limit */
  thisBookingMinutes: number
  /** Hours equivalent (2 decimals) */
  thisBookingHours: number
  /** Minutes from this booking that exceed the limit (0 if no overflow) */
  overageMinutes: number
  /** Hours equivalent (2 decimals) */
  overageHours: number
}

/**
 * The session row under a booking preview. A booking consumes exactly one
 * slot, so `thisBookingSessions` is 1 when a slot is free and `overageSessions`
 * is 1 when none is — never a partial amount.
 */
export interface BookingSessionsLimitRow extends SessionsLimitRow {
  /** 1 when a slot is available for this booking, else 0 */
  thisBookingSessions: number
  /** 1 when this booking exceeds the cap, else 0 */
  overageSessions: number
}

export type BookingUsageLimitRow = BookingHoursLimitRow | BookingSessionsLimitRow

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Compute used minutes from max and remaining.
 * If remaining is null (shouldn't happen when max is set), defaults to 0 used.
 */
function computeUsed(max: number, remaining: number | null): number {
  return Math.max(0, max - (remaining ?? max))
}

// ============================================================================
// BUILDER
// ============================================================================

/**
 * Build an array of configured usage-limit rows from usage data.
 *
 * Only limits with a non-null `max` value are included. Order:
 * active → day → week → month.
 *
 * `usedMinutes` is derived internally from `max − remaining` so
 * the function works with any object satisfying `UsageLimitsInput`
 * (both `SubscriptionUsageStats` and `UsageInfo`).
 *
 * @example
 * ```ts
 * // With SubscriptionUsageStats
 * const rows = buildUsageLimits(activeSubscription.usage)
 *
 * // With UsageInfo (from booking validation)
 * const rows = buildUsageLimits(usageInfo)
 * ```
 */
export function buildUsageLimits(
  usage: UsageLimitsInput,
): UsageLimitRow[] {
  const rows: UsageLimitRow[] = []

  if (usage.maxActiveMinutes !== null) {
    const remainingActive = usage.remainingMinutesActive ?? usage.remainingActiveMinutes ?? null
    const used = computeUsed(usage.maxActiveMinutes, remainingActive)
    rows.push({
      unit: 'hours',
      period: 'active',
      maxMinutes: usage.maxActiveMinutes,
      usedMinutes: used,
      remainingMinutes: remainingActive,
      maxHours: roundHours(usage.maxActiveMinutes, 2),
      usedHours: roundHours(used, 2),
      remainingHours: remainingActive !== null
        ? roundHours(remainingActive, 2)
        : null,
    })
  }

  if (usage.maxMinutesPerDay !== null) {
    const used = computeUsed(usage.maxMinutesPerDay, usage.remainingMinutesToday)
    rows.push({
      unit: 'hours',
      period: 'day',
      maxMinutes: usage.maxMinutesPerDay,
      usedMinutes: used,
      remainingMinutes: usage.remainingMinutesToday,
      maxHours: roundHours(usage.maxMinutesPerDay, 2),
      usedHours: roundHours(used, 2),
      remainingHours: usage.remainingMinutesToday !== null
        ? roundHours(usage.remainingMinutesToday, 2)
        : null,
    })
  }

  if (usage.maxMinutesPerWeek !== null) {
    const used = computeUsed(usage.maxMinutesPerWeek, usage.remainingMinutesThisWeek)
    rows.push({
      unit: 'hours',
      period: 'week',
      maxMinutes: usage.maxMinutesPerWeek,
      usedMinutes: used,
      remainingMinutes: usage.remainingMinutesThisWeek,
      maxHours: roundHours(usage.maxMinutesPerWeek, 2),
      usedHours: roundHours(used, 2),
      remainingHours: usage.remainingMinutesThisWeek !== null
        ? roundHours(usage.remainingMinutesThisWeek, 2)
        : null,
    })
  }

  if (usage.maxMinutesPerMonth !== null) {
    const used = computeUsed(usage.maxMinutesPerMonth, usage.remainingMinutesThisMonth)
    rows.push({
      unit: 'hours',
      period: 'month',
      maxMinutes: usage.maxMinutesPerMonth,
      usedMinutes: used,
      remainingMinutes: usage.remainingMinutesThisMonth,
      maxHours: roundHours(usage.maxMinutesPerMonth, 2),
      usedHours: roundHours(used, 2),
      remainingHours: usage.remainingMinutesThisMonth !== null
        ? roundHours(usage.remainingMinutesThisMonth, 2)
        : null,
    })
  }

  // Counts, not minutes — see SessionsLimitRow. `!= null` because a payload
  // predating this pool arrives with the field absent, not null.
  if (usage.maxActiveSessions != null) {
    const remainingSessions = usage.remainingActiveSessions ?? null
    rows.push({
      unit: 'sessions',
      period: 'activeSessions',
      maxSessions: usage.maxActiveSessions,
      usedSessions: computeUsed(usage.maxActiveSessions, remainingSessions),
      remainingSessions,
    })
  }

  return rows
}

// ============================================================================
// BOOKING AUGMENTATION
// ============================================================================

/**
 * Augment usage-limit rows with per-period booking preview fields.
 *
 * For each row, computes how much of `bookingDurationMinutes` fits within
 * the remaining capacity (`thisBooking`) and how much exceeds the limit
 * (`overage`). Every period is treated uniformly — no special cases.
 *
 * @param rows - Base rows from `buildUsageLimits`
 * @param bookingDurationMinutes - Duration of the booking being previewed
 * @returns Augmented rows with `thisBooking` and `overage` fields
 *
 * @example
 * ```ts
 * const rows = buildUsageLimits(usageInfo)
 * const augmented = augmentWithBooking(rows, 60)
 * // augmented[0].thisBookingMinutes → 30 (covered)
 * // augmented[0].overageMinutes     → 30 (overflow)
 * ```
 */
export function augmentWithBooking(
  rows: UsageLimitRow[],
  bookingDurationMinutes: number,
): BookingUsageLimitRow[] {
  return rows.map((row) => {
    // A session is indivisible: the booking either fits in a free slot or it
    // doesn't. No partial covered amount to compute.
    if (row.unit === 'sessions') {
      const hasSlot = (row.remainingSessions ?? 0) > 0
      return {
        ...row,
        thisBookingSessions: hasSlot ? 1 : 0,
        overageSessions: hasSlot ? 0 : 1,
      }
    }

    const remaining = row.remainingMinutes ?? Infinity
    const covered = Math.min(bookingDurationMinutes, Math.max(0, remaining))
    const overflow = Math.max(0, bookingDurationMinutes - Math.max(0, remaining))

    return {
      ...row,
      thisBookingMinutes: covered,
      thisBookingHours: roundHours(covered, 2),
      overageMinutes: overflow,
      overageHours: roundHours(overflow, 2),
    }
  })
}
